import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { DashboardShell } from "@/components/DashboardShell";
import { CUSTOMER_NAV } from "@/lib/nav";
import { EmptyState } from "@/components/states";
import { useAuth } from "@/lib/auth";
import { SERVICE_LABEL } from "@/lib/services";
import { ENQUIRY_STATUS_CLASS, ENQUIRY_STATUS_LABEL, listCustomerEnquiries } from "@/lib/enquiries";

export const Route = createFileRoute("/_authenticated/customer/enquiries")({
  head: () => ({
    meta: [
      { title: "My enquiries · Landoo" },
      {
        name: "description",
        content: "Track the enquiries you sent to Landoo partners and read their replies.",
      },
      { property: "og:title", content: "My enquiries · Landoo" },
      {
        property: "og:description",
        content: "Track the enquiries you sent to Landoo partners and read their replies.",
      },
    ],
  }),
  component: CustomerEnquiriesPage,
});

function CustomerEnquiriesPage() {
  const { user } = useAuth();
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["customer", "enquiries", user?.id],
    queryFn: () => listCustomerEnquiries(user!.id),
    enabled: !!user,
  });

  return (
    <DashboardShell title="My enquiries" role="Customer" items={CUSTOMER_NAV}>
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading enquiries…</p>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No enquiries yet"
          description="Start from the map, open a listing and send an enquiry."
        />
      ) : (
        <div className="grid gap-2">
          {rows.map((e) => (
            <div key={e.id} className="rounded-2xl border border-border bg-card p-4 shadow-soft">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="rounded-full bg-brand-soft px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-brown-dark">
                    {SERVICE_LABEL[e.service_type]}
                  </span>
                  <span
                    className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${ENQUIRY_STATUS_CLASS[e.enquiry_status]}`}
                  >
                    {ENQUIRY_STATUS_LABEL[e.enquiry_status]}
                  </span>
                </div>
                <span className="text-[11px] text-muted-foreground">
                  {new Date(e.created_at).toLocaleString()}
                </span>
              </div>

              <div className="mt-2 text-sm font-semibold text-brown-dark">
                {e.listing_title ?? e.listing_kind}
              </div>
              {e.message && <p className="mt-1.5 text-xs text-brown-dark">{e.message}</p>}

              {e.partner_reply && (
                <div className="mt-2 rounded-xl bg-secondary p-2.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Partner reply
                  </p>
                  <p className="mt-1 text-xs text-brown-dark">{e.partner_reply}</p>
                </div>
              )}

              {e.listing_id && (
                <Link
                  to="/property/$id"
                  params={{ id: e.listing_id }}
                  className="mt-3 inline-flex rounded-xl border border-border px-4 py-2 text-xs font-semibold text-brown-dark transition hover:bg-secondary"
                >
                  View listing
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}
